import { QuestionCircleOutlined } from '@ant-design/icons';
import { parseHtml, twClassMerge } from '@healthhub/react-ui';
import DOMPurify from 'dompurify';
import React from 'react';

import useToggle from '@shared/hooks/useToggle/useToggle';
import type { Helper } from '@shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import { formatListStyleAsHtml } from '@shared/utils/string';

import { checkHtmlContainsString } from '@features/Appointment/utils/tools';

import HelperQuestionnaireModal from './HelperQuestionnaireModal';

interface Props {
  children: React.ReactElement;
  description?: string;
  helperNote?: Helper | null;
  isInputQuestion?: boolean;
  question?: string;
  questionNumber?: number;
  questionStyle?: string;
  required: boolean;
  requiredMarkPosition?: 'end' | 'start';
}

const QuestionForm = ({
  children,
  description,
  helperNote,
  isInputQuestion = true,
  question,
  questionNumber,
  questionStyle,
  required,
  requiredMarkPosition = 'start',
}: Props) => {
  const [isVisible, onToggle] = useToggle();

  return (
    <>
      <div className="flex flex-col">
        {!!question && (
          <div className="gbmask mb-2 flex">
            {requiredMarkPosition === 'start' && !!required && <span className="mr-1 text-error-color"> *</span>}
            {checkHtmlContainsString(question) ? (
              <div>
                <div className="text-sm text-functional-iron md:text-base [&_a]:text-theme-600 [&_ol]:mt-4 [&_ol]:flex [&_ol]:flex-col [&_ol]:gap-3 [&_ol]:px-6 [&_p]:text-sm [&_ul]:mt-2 [&_ul]:list-hyphenate [&_ul]:space-y-1 [&_ul]:pl-2">
                  {parseHtml(
                    DOMPurify.sanitize(formatListStyleAsHtml(question), {
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
                    })
                  )}
                </div>
                {!!helperNote && <QuestionCircleOutlined className="ml-2 cursor-pointer" onClick={onToggle} />}
              </div>
            ) : (
              <span
                className={twClassMerge(
                  'text-sm md:text-base text-functional-iron language-ta:break-all',
                  questionStyle
                )}
              >
                {!!questionNumber && <span className="text-base text-functional-iron">{questionNumber}.&nbsp;</span>}
                {question}
                {requiredMarkPosition === 'end' && !!required && <span className="mr-1 text-error-color"> *</span>}
                {!!helperNote && <QuestionCircleOutlined className="ml-2 cursor-pointer" onClick={onToggle} />}
              </span>
            )}
          </div>
        )}
        {!!description && !isInputQuestion && (
          <div className="mb-2 ml-2 text-xs text-functional-iron">{description}</div>
        )}
        {children}
        {!!description && !!isInputQuestion && (
          <div className="mb-6 ml-4 mt-[-16px] text-xs text-functional-iron">{description}</div>
        )}
      </div>
      <HelperQuestionnaireModal content={helperNote?.Text ?? ''} onCancel={onToggle} onOk={onToggle} open={isVisible} />
    </>
  );
};

export default QuestionForm;
